import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Settings, Plus, Edit, Trash2, ChevronLeft, Loader2, 
  Cpu, Layers, Palette, Truck, Save, Lock, Eye, EyeOff, Clock, DollarSign
} from 'lucide-react';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [technologies, setTechnologies] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [postProcesses, setPostProcesses] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  
  const [editingItem, setEditingItem] = useState(null);
  const [editType, setEditType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Design rate state
  const [designRate, setDesignRate] = useState(85);
  const [savingDesignRate, setSavingDesignRate] = useState(false);

  useEffect(() => {
    loadAllSettings();
    loadDesignRate();
  }, []);

  const loadDesignRate = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/design-rate`, getAuthHeaders());
      setDesignRate(response.data.hourly_rate || 85);
    } catch (error) {
      console.error('Failed to load design rate:', error);
    }
  };

  const saveDesignRate = async () => {
    setSavingDesignRate(true);
    try {
      await axios.put(`${API}/admin/settings/design-rate`, {
        hourly_rate: parseFloat(designRate) || 85
      }, getAuthHeaders());
      toast.success('Design rate updated successfully');
    } catch (error) {
      toast.error('Failed to update design rate');
    }
    setSavingDesignRate(false);
  };

  const loadAllSettings = async () => {
    try {
      const [techRes, matRes, procRes, delRes] = await Promise.all([
        axios.get(`${API}/settings/technologies`),
        axios.get(`${API}/settings/materials`),
        axios.get(`${API}/settings/post-processes`),
        axios.get(`${API}/settings/delivery-options`)
      ]);
      
      setTechnologies(techRes.data.technologies || []);
      setMaterials(matRes.data.materials || []);
      setPostProcesses(procRes.data.post_processes || []);
      setDeliveryOptions(delRes.data.delivery_options || []);
    } catch (error) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };
  
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, getAuthHeaders());
      
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
    setChangingPassword(false);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
  });

  const handleSave = async (type, data) => {
    setSaving(true);
    try {
      const endpoint = {
        technology: '/settings/technologies',
        material: '/settings/materials',
        process: '/settings/post-processes',
        delivery: '/settings/delivery-options'
      }[type];

      const idField = {
        technology: 'tech_id',
        material: 'material_id',
        process: 'process_id',
        delivery: 'delivery_id'
      }[type];

      if (data[idField]) {
        // Update
        await axios.put(`${API}${endpoint}/${data[idField]}`, data, getAuthHeaders());
        toast.success('Updated successfully');
      } else {
        // Create
        await axios.post(`${API}${endpoint}`, data, getAuthHeaders());
        toast.success('Created successfully');
      }
      
      loadAllSettings();
      setDialogOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (type, id) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const endpoint = {
        technology: `/settings/technologies/${id}`,
        material: `/settings/materials/${id}`,
        process: `/settings/post-processes/${id}`,
        delivery: `/settings/delivery-options/${id}`
      }[type];

      await axios.delete(`${API}${endpoint}`, getAuthHeaders());
      toast.success('Deleted successfully');
      
      // Immediately update local state to remove deleted item
      // Backend does soft delete (sets active: false), but we filter active items on fetch
      // So we just remove from local state immediately
      switch(type) {
        case 'technology':
          setTechnologies(prevTechs => prevTechs.filter(t => t.tech_id !== id));
          // Also remove materials linked to this technology
          setMaterials(prevMats => prevMats.filter(m => m.technology_id !== id));
          break;
        case 'material':
          setMaterials(prevMats => prevMats.filter(m => m.material_id !== id));
          // Also remove post-processes linked to this material
          setPostProcesses(prevProcs => prevProcs.filter(p => p.material_id !== id));
          break;
        case 'process':
          setPostProcesses(prevProcs => prevProcs.filter(p => p.process_id !== id));
          break;
        case 'delivery':
          setDeliveryOptions(prevDels => prevDels.filter(d => d.delivery_id !== id));
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditDialog = (type, item = null) => {
    setEditType(type);
    setEditingItem(item || getEmptyItem(type));
    setDialogOpen(true);
  };

  const getEmptyItem = (type) => {
    switch (type) {
      case 'technology':
        return { name: '', description: '', active: true };
      case 'material':
        return { name: '', technology_id: '', price_per_cm3: 0, color: '', description: '', active: true };
      case 'process':
        return { name: '', material_id: '', price_per_unit: 0, description: '', active: true };
      case 'delivery':
        return { name: '', price: 0, estimated_days: 0, description: '', active: true };
      default:
        return {};
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-settings">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/admin">
              <Button variant="ghost" className="text-white/50 hover:text-white p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <span className="overline text-[#CCFF00]">Admin</span>
              <h1 className="text-3xl font-['Chivo'] font-black text-white mt-1">
                Pricing Settings
              </h1>
            </div>
          </div>

          <Tabs defaultValue="general" className="space-y-6">
            <TabsList className="bg-[#111] border border-white/10 p-1">
              <TabsTrigger value="general" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Settings className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger value="technologies" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Cpu className="w-4 h-4 mr-2" />
                Technologies
              </TabsTrigger>
              <TabsTrigger value="materials" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Layers className="w-4 h-4 mr-2" />
                Materials
              </TabsTrigger>
              <TabsTrigger value="processes" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Palette className="w-4 h-4 mr-2" />
                Post Process
              </TabsTrigger>
              <TabsTrigger value="delivery" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Truck className="w-4 h-4 mr-2" />
                Delivery
              </TabsTrigger>
              <TabsTrigger value="account" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black">
                <Lock className="w-4 h-4 mr-2" />
                Account
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general">
              <div className="bg-[#111] border border-white/10 rounded-sm">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#CCFF00]" />
                    Design Rate
                  </h3>
                  <p className="text-white/50 text-sm mt-1">Hourly rate charged for design work on manual orders</p>
                </div>
                <div className="p-6 max-w-md">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white/70">Hourly Rate (AUD)</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={designRate}
                          onChange={(e) => setDesignRate(e.target.value)}
                          className="bg-white/5 border-white/10 text-white pl-9"
                          placeholder="85.00"
                          data-testid="design-rate-input"
                        />
                      </div>
                      <p className="text-white/30 text-xs mt-2">
                        This rate is used when creating orders for walk-in/email customers with design hours
                      </p>
                    </div>
                    <Button
                      onClick={saveDesignRate}
                      disabled={savingDesignRate}
                      className="bg-[#CCFF00] text-black hover:bg-[#b8e600] rounded-none"
                      data-testid="save-design-rate-btn"
                    >
                      {savingDesignRate ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Design Rate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Technologies Tab */}
            <TabsContent value="technologies">
              <div className="bg-[#111] border border-white/10 rounded-sm">
                <div className="p-6 border-b border-white/10 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-white">Printing Technologies</h2>
                  <Button 
                    onClick={() => openEditDialog('technology')}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                    data-testid="add-technology-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Technology
                  </Button>
                </div>
                <div className="divide-y divide-white/10">
                  {technologies.map((tech) => (
                    <div key={tech.tech_id} className="p-6 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{tech.name}</p>
                        <p className="text-white/50 text-sm">{tech.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded ${tech.active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                          {tech.active ? 'Active' : 'Inactive'}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog('technology', tech)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('technology', tech.tech_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {technologies.length === 0 && (
                    <div className="p-12 text-center text-white/50">
                      No technologies configured. Click &quot;Add Technology&quot; to create one.
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Materials Tab */}
            <TabsContent value="materials">
              <div className="bg-[#111] border border-white/10 rounded-sm">
                <div className="p-6 border-b border-white/10 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-white">Materials</h2>
                  <Button 
                    onClick={() => openEditDialog('material')}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                    data-testid="add-material-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Material
                  </Button>
                </div>
                <div className="divide-y divide-white/10">
                  {materials.map((mat) => (
                    <div key={mat.material_id} className="p-6 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{mat.name}</p>
                        <p className="text-white/50 text-sm">
                          ${mat.price_per_cm3}/cm³ • {technologies.find(t => t.tech_id === mat.technology_id)?.name || 'Unknown'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#CCFF00] font-mono">${mat.price_per_cm3}/cm³</span>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog('material', mat)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('material', mat.material_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {materials.length === 0 && (
                    <div className="p-12 text-center text-white/50">
                      No materials configured.
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Post Processes Tab */}
            <TabsContent value="processes">
              <div className="bg-[#111] border border-white/10 rounded-sm">
                <div className="p-6 border-b border-white/10 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-white">Post Processing Options</h2>
                  <Button 
                    onClick={() => openEditDialog('process')}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                    data-testid="add-process-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Process
                  </Button>
                </div>
                <div className="divide-y divide-white/10">
                  {postProcesses.map((proc) => (
                    <div key={proc.process_id} className="p-6 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{proc.name}</p>
                        <p className="text-white/50 text-sm">{proc.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#CCFF00] font-mono">+${proc.price_per_unit}</span>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog('process', proc)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('process', proc.process_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {postProcesses.length === 0 && (
                    <div className="p-12 text-center text-white/50">
                      No post processes configured.
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Delivery Tab */}
            <TabsContent value="delivery">
              <div className="bg-[#111] border border-white/10 rounded-sm">
                <div className="p-6 border-b border-white/10 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-white">Delivery Options</h2>
                  <Button 
                    onClick={() => openEditDialog('delivery')}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                    data-testid="add-delivery-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Option
                  </Button>
                </div>
                <div className="divide-y divide-white/10">
                  {deliveryOptions.map((opt) => (
                    <div key={opt.delivery_id} className="p-6 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{opt.name}</p>
                        <p className="text-white/50 text-sm">{opt.estimated_days} day(s) • {opt.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#CCFF00] font-mono">${opt.price.toFixed(2)}</span>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog('delivery', opt)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('delivery', opt.delivery_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {deliveryOptions.length === 0 && (
                    <div className="p-12 text-center text-white/50">
                      No delivery options configured.
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account">
              <div className="bg-[#111] border border-white/10 rounded-sm">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#CCFF00]" />
                    Change Password
                  </h3>
                  <p className="text-white/50 text-sm mt-1">Update your admin account password</p>
                </div>
                <div className="p-6 space-y-4 max-w-md">
                  <div>
                    <Label className="text-white/70">Current Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        className="bg-white/5 border-white/10 text-white pr-10"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/70">New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="bg-white/5 border-white/10 text-white pr-10"
                        placeholder="Enter new password (min 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/70">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="bg-[#CCFF00] text-black hover:bg-[#b8e600] rounded-none mt-2"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Edit Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="bg-[#111] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle>
                  {editingItem?.tech_id || editingItem?.material_id || editingItem?.process_id || editingItem?.delivery_id 
                    ? 'Edit' : 'Add'} {editType.charAt(0).toUpperCase() + editType.slice(1)}
                </DialogTitle>
              </DialogHeader>
              
              {editType === 'technology' && (
                <TechnologyForm 
                  item={editingItem} 
                  onSave={(data) => handleSave('technology', data)}
                  saving={saving}
                />
              )}
              {editType === 'material' && (
                <MaterialForm 
                  item={editingItem} 
                  technologies={technologies}
                  onSave={(data) => handleSave('material', data)}
                  saving={saving}
                />
              )}
              {editType === 'process' && (
                <ProcessForm 
                  item={editingItem} 
                  materials={materials}
                  onSave={(data) => handleSave('process', data)}
                  saving={saving}
                />
              )}
              {editType === 'delivery' && (
                <DeliveryForm 
                  item={editingItem} 
                  onSave={(data) => handleSave('delivery', data)}
                  saving={saving}
                />
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Form Components
const TechnologyForm = ({ item, onSave, saving }) => {
  const [data, setData] = useState(item);

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-white/70">Name *</Label>
        <Input
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="HP Multi Jet Fusion"
        />
      </div>
      <div>
        <Label className="text-white/70">Description</Label>
        <Input
          value={data.description || ''}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="Industrial-grade nylon printing"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={data.active}
          onChange={(e) => setData({ ...data, active: e.target.checked })}
          className="w-4 h-4"
        />
        <Label className="text-white/70">Active</Label>
      </div>
      <Button 
        onClick={() => onSave(data)} 
        disabled={saving || !data.name}
        className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600]"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save
      </Button>
    </div>
  );
};

const MaterialForm = ({ item, technologies, onSave, saving }) => {
  const [data, setData] = useState(item);

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-white/70">Name *</Label>
        <Input
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="PA12 Nylon"
        />
      </div>
      <div>
        <Label className="text-white/70">Technology *</Label>
        <Select value={data.technology_id} onValueChange={(v) => setData({ ...data, technology_id: v })}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
            <SelectValue placeholder="Select technology" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            {technologies.map(t => (
              <SelectItem key={t.tech_id} value={t.tech_id} className="text-white">
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-white/70">Price per cm³ *</Label>
        <Input
          type="number"
          step="0.01"
          value={data.price_per_cm3}
          onChange={(e) => setData({ ...data, price_per_cm3: parseFloat(e.target.value) || 0 })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="0.85"
        />
      </div>
      <div>
        <Label className="text-white/70">Color</Label>
        <Input
          value={data.color || ''}
          onChange={(e) => setData({ ...data, color: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="Grey"
        />
      </div>
      <Button 
        onClick={() => onSave(data)} 
        disabled={saving || !data.name || !data.technology_id}
        className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600]"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save
      </Button>
    </div>
  );
};

const ProcessForm = ({ item, materials, onSave, saving }) => {
  const [data, setData] = useState(item);

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-white/70">Name *</Label>
        <Input
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="Dye Black"
        />
      </div>
      <div>
        <Label className="text-white/70">Material *</Label>
        <Select value={data.material_id} onValueChange={(v) => setData({ ...data, material_id: v })}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
            <SelectValue placeholder="Select material" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            {materials.map(m => (
              <SelectItem key={m.material_id} value={m.material_id} className="text-white">
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-white/70">Price per Unit *</Label>
        <Input
          type="number"
          step="0.01"
          value={data.price_per_unit}
          onChange={(e) => setData({ ...data, price_per_unit: parseFloat(e.target.value) || 0 })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="5.00"
        />
      </div>
      <Button 
        onClick={() => onSave(data)} 
        disabled={saving || !data.name || !data.material_id}
        className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600]"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save
      </Button>
    </div>
  );
};

const DeliveryForm = ({ item, onSave, saving }) => {
  const [data, setData] = useState(item);

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-white/70">Name *</Label>
        <Input
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="Standard Shipping"
        />
      </div>
      <div>
        <Label className="text-white/70">Price *</Label>
        <Input
          type="number"
          step="0.01"
          value={data.price}
          onChange={(e) => setData({ ...data, price: parseFloat(e.target.value) || 0 })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="15.00"
        />
      </div>
      <div>
        <Label className="text-white/70">Estimated Days *</Label>
        <Input
          type="number"
          value={data.estimated_days}
          onChange={(e) => setData({ ...data, estimated_days: parseInt(e.target.value) || 0 })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="5"
        />
      </div>
      <div>
        <Label className="text-white/70">Description</Label>
        <Input
          value={data.description || ''}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          className="bg-white/5 border-white/10 text-white mt-1"
          placeholder="Australia Post standard"
        />
      </div>
      <Button 
        onClick={() => onSave(data)} 
        disabled={saving || !data.name}
        className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600]"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save
      </Button>
    </div>
  );
};

export default AdminSettings;
